import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import InfoButton from 'components/InfoButton';
import { addDataLayer } from '../lib/GoogleTagManager';

const infoButtonSize = 20;
const Button = styled(
  ({
    className,
    fullWidth,
    inverse,
    disabled,
    disableReason,
    children,
    dataLayer,
    onClick,
    ...rest
  }) => {
    const handleClick = (event) => {
      addDataLayer(dataLayer);
      if (onClick) {
        onClick(event);
      }
    };

    const classes = ['Button'];
    const buttonRef = React.useRef();
    if (className) classes.push(className);
    return (
      // eslint-disable-next-line react/button-has-type
      <div className={classes.join(' ')}>
        <button
          className="button"
          disabled={disabled || !!disableReason}
          onClick={handleClick}
          type="button"
          {...rest}
          ref={buttonRef}
        >
          {children}
        </button>
        {disableReason && (
          <InfoButton overlapElementRef={buttonRef} offset={20}>
            {disableReason}
          </InfoButton>
        )}
      </div>
    );
  },
)`
  position: relative;
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  .button {
    border: none;
    width: 100%;
    height: 52px;
    min-width: 136px;
    padding: 0 48px;
    border: 3px solid ${({ theme }) => theme.palette.primary.main};
    border-radius: 2px;
    font-size: 1rem;
    font-weight: 700;
    outline: none;
    transition: color 0.3s ease, border-color 0.3s ease,
      background-color 0.3s ease;
    font-family: ${({ theme }) => theme.typography.display.fontFamily};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  ${({ theme, inverse }) => `
      display:${inverse ? 'inline-block' : 'block'};
      .button {
      ${
  inverse
    ? `
            color: ${theme.palette.primary.main};
            background-color: ${theme.palette.primary.contrastText};
            &:enabled {
              &:hover,:focus,:active {
                background-color: ${theme.palette.primary.dark};
                color: ${theme.palette.primary.contrastText};
              }
            }
            &:disabled {
              color: ${theme.palette.input.disabled};
              border-color: ${theme.palette.input.disabled};
            }
          }
        `
    : `
          color: ${theme.palette.primary.contrastText};
          background-color: ${theme.palette.primary.main};
          &:hover,:focus,:active {
            background-color: ${theme.palette.primary.dark};
          }
          &:disabled {
            background-color: ${theme.palette.input.disabled};
            border-color: ${theme.palette.input.disabled};
          }
        }`
}

  ${InfoButton} {
    ${
  inverse
    ? `color: ${theme.palette.input.disabled};`
    : `color: ${theme.palette.primary.contrastText};`
}
    .svgIcon{
      font-size: ${infoButtonSize}px;
    }
    position: absolute;
    right: ${theme.spacing}px;
    top: calc(50% - ${infoButtonSize / 2}px);
  }
`}
`;

Button.displayName = 'Button';

Button.propTypes = {
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  inverse: PropTypes.bool,
  dataLayer: PropTypes.shape({
    action: PropTypes.string,
    label: PropTypes.string,
  }),
};

Button.defaultProps = {
  className: null,
  fullWidth: false,
  inverse: false,
  dataLayer: null,
};

export default Button;
